import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import { Loader2, ArrowRight } from "lucide-react";

const Login = () => {
    const { user, login, loading } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("admin@yugo.in");
    const [password, setPassword] = useState("Admin@123");
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (loading) return null;
    if (user) return <Navigate to="/dashboard" replace />;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setSubmitting(true);
        try {
            await login(email.trim(), password);
            navigate("/dashboard");
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left – form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
                <div className="w-full max-w-sm">
                    <div className="flex items-center gap-2 mb-10">
                        <div className="w-10 h-10 rounded-xl bg-mint-500 flex items-center justify-center font-display font-bold text-white text-xl">Y</div>
                        <div>
                            <div className="font-display font-bold text-navy-900 text-xl leading-tight">YuGo</div>
                            <div className="text-[11px] tracking-[0.2em] text-mint-600 font-semibold">MUSAFIR</div>
                        </div>
                    </div>

                    <h1 className="font-display font-bold text-3xl text-navy-900 mb-2 tracking-tight">Welcome back</h1>
                    <p className="text-sm text-slate-500 mb-8">Sign in to the YuGo operations console.</p>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-mint-500/30 focus:border-mint-500 transition"
                                placeholder="you@yugo.in"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-mint-500/30 focus:border-mint-500 transition"
                                placeholder="••••••••"
                                data-testid="login-password-input"
                            />
                        </div>

                        {err && (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                                {err}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-mint-500 hover:bg-mint-600 text-white font-semibold py-3 rounded-full transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                            data-testid="login-submit-btn"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
                        </button>
                    </form>

                    <div className="mt-10 text-xs text-slate-400">
                        Default super admin: <span className="font-mono text-slate-600">admin@yugo.in</span> / <span className="font-mono text-slate-600">Admin@123</span>
                    </div>
                </div>
            </div>

            {/* Right – brand panel */}
            <div className="hidden lg:flex w-1/2 mint-hero relative items-center justify-center p-12 overflow-hidden">
                <div className="max-w-md relative z-10">
                    <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-mint-200 px-3 py-1 rounded-full text-xs font-semibold text-mint-700 mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-mint-500" />
                        Operations Console
                    </div>
                    <h2 className="font-display font-bold text-4xl text-navy-900 leading-tight tracking-tight mb-4">
                        Run the city. <span className="text-mint-600">One ride at a time.</span>
                    </h2>
                    <p className="text-slate-600 leading-relaxed">
                        Monitor live rides, approve drivers, manage fares and keep an eye on every rupee of revenue — across Patna and beyond.
                    </p>
                    <div className="grid grid-cols-3 gap-4 mt-10">
                        {[
                            { k: "500+", v: "Riders" },
                            { k: "4.8★", v: "Avg rating" },
                            { k: "24×7", v: "Operations" },
                        ].map((s) => (
                            <div key={s.v} className="bg-white/80 backdrop-blur border border-mint-100 rounded-2xl px-4 py-3">
                                <div className="font-display font-bold text-navy-900 text-lg">{s.k}</div>
                                <div className="text-xs text-slate-500">{s.v}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-mint-300/30 rounded-full blur-3xl" />
                <div className="absolute -top-24 -left-24 w-72 h-72 bg-mint-400/20 rounded-full blur-3xl" />
            </div>
        </div>
    );
};

export default Login;
